import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { X, Save, Ban, AlertTriangle, Camera } from 'lucide-react';

const EditProfileModal = ({ isOpen, onClose, userData, triggerPhotoSelector }) => {
  const [formData, setFormData] = useState({ firstName: '', lastName: '', username: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const isPaidTier = userData?.tier && userData.tier !== 'DREAMER'; 

  useEffect(() => {
    if (userData) {
      setFormData({
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        username: userData.username || ''
      });
    }
  }, [userData]);

  if (!isOpen) return null;

  const handleSave = async () => {
    setError('');
    if (!auth.currentUser || !userData) {
        setError("Security Session Error.");
        return;
    }

    // MANUAL CHECK (Simplified - No Library)
    if (formData.username !== userData?.username) {
        if (!isPaidTier) {
            setError("Upgrade to Architect Tier to change Codename.");
            return;
        }
        const usernameRegex = /^[a-zA-Z0-9]{3,15}$/;
        if (!usernameRegex.test(formData.username)) {
            setError("Username must be 3-15 letters/numbers only.");
            return;
        }
    }

    setLoading(true);
    try {
      const userRef = doc(db, 'users', auth.currentUser.uid);
      await updateDoc(userRef, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        username: formData.username
      });
      onClose(); 
    } catch (err) {
      setError("Update failed.");
    }
    setLoading(false);
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h2 style={styles.title}>EDIT IDENTITY</h2>
          <button onClick={onClose} style={styles.closeBtn}><X size={24} /></button>
        </div>

        {error && (
            <div style={styles.errorBanner}>
                <AlertTriangle size={16} style={{marginRight: '10px'}} />
                {error}
            </div>
        )}

        <div style={styles.contentRow}>
            {/* LEFT: PROFILE PIC EDITOR */}
            <div style={styles.picSection}>
                <img src={userData?.profilePic || 'https://placehold.co/150'} style={styles.previewPic} alt="Profile" />
                <button onClick={() => { onClose(); triggerPhotoSelector(); }} style={styles.changePhotoBtn}>
                    <Camera size={14} style={{marginRight: 5}}/> CHANGE PHOTO
                </button>
            </div>

            {/* RIGHT: FORM */}
            <div style={styles.formSection}>
                <div style={styles.fieldGroup}>
                    <label style={styles.label}>FIRST NAME</label>
                    <input style={styles.input} value={formData.firstName} onChange={(e) => setFormData({...formData, firstName: e.target.value})} />
                </div>

                <div style={styles.fieldGroup}>
                    <label style={styles.label}>LAST NAME</label>
                    <input style={styles.input} value={formData.lastName} onChange={(e) => setFormData({...formData, lastName: e.target.value})} />
                </div>

                <div style={styles.fieldGroup}>
                    <label style={styles.label}>CODENAME {isPaidTier ? '(EDITABLE)' : '(LOCKED)'}</label>
                    <div style={{position: 'relative'}}>
                        <input 
                            style={isPaidTier ? styles.input : styles.lockedInput} 
                            value={formData.username} 
                            onChange={(e) => setFormData({...formData, username: e.target.value})}
                            readOnly={!isPaidTier}
                        />
                        {!isPaidTier && <div style={{position: 'absolute', right: '10px', top: '12px'}}><Ban size={16} color="#444"/></div>}
                    </div>
                </div>
            </div>
        </div>

        <div style={styles.actions}>
            <button onClick={onClose} style={styles.cancelBtn}>CANCEL</button>
            <button onClick={handleSave} disabled={loading} style={styles.saveBtn}>
                <Save size={16} style={{marginRight: '8px'}} />
                {loading ? 'SAVING...' : 'SAVE CHANGES'}
            </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: { position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.9)', zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'center', backdropFilter: 'blur(10px)' },
  modal: { background: '#000', border: '1px solid #333', width: '90%', maxWidth: '600px', borderRadius: '12px', padding: '30px', boxShadow: '0 10px 40px rgba(0,0,0,0.5)' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' },
  title: { color: '#fff', fontFamily: 'Orbitron', margin: 0, fontSize: '1.2rem' },
  closeBtn: { background: 'transparent', border: 'none', color: '#666', cursor: 'pointer' },
  errorBanner: { background: '#200', color: '#ff4d4d', padding: '12px', fontSize: '0.8rem', marginBottom: '20px', border: '1px solid #400', borderRadius: '4px', display: 'flex', alignItems: 'center', fontWeight: 'bold' },
  contentRow: { display: 'flex', gap: '30px', flexDirection: 'row', alignItems: 'flex-start' },
  picSection: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '15px' },
  previewPic: { width: '100px', height: '100px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #D4AF37' },
  changePhotoBtn: { background: '#111', border: '1px solid #D4AF37', color: '#D4AF37', padding: '8px 12px', fontSize: '0.7rem', fontFamily: 'Orbitron', cursor: 'pointer', display: 'flex', alignItems: 'center' },
  formSection: { flex: 1, display: 'flex', flexDirection: 'column', gap: '15px' },
  fieldGroup: { display: 'flex', flexDirection: 'column', gap: '8px' },
  label: { color: '#D4AF37', fontSize: '0.7rem', fontFamily: 'Orbitron', letterSpacing: '1px' },
  input: { background: '#111', border: '1px solid #333', color: '#fff', padding: '12px', borderRadius: '4px', outline: 'none', fontFamily: 'Courier New', width: '100%', boxSizing: 'border-box' },
  lockedInput: { background: '#050505', border: '1px solid #222', color: '#444', padding: '12px', borderRadius: '4px', outline: 'none', fontFamily: 'Courier New', cursor: 'not-allowed', width: '100%', boxSizing: 'border-box' },
  actions: { display: 'flex', gap: '15px', marginTop: '30px' },
  cancelBtn: { flex: 1, background: 'transparent', border: '1px solid #444', color: '#888', padding: '12px', cursor: 'pointer', fontFamily: 'Orbitron', fontWeight: 'bold' },
  saveBtn: { flex: 1, background: '#D4AF37', color: '#000', border: 'none', padding: '12px', cursor: 'pointer', fontFamily: 'Orbitron', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center' }
};

export default EditProfileModal;
